import type { TokenPair } from '@tasks/contracts/auth';

const REFRESH_KEY = 'tasks.refresh';

export type Session = { accessToken: string; accessExpiresAt: number };

/**
 * El access vive solo en memoria; el refresh en localStorage por decisión explícita del brief.
 * Riesgo aceptado: un XSS se lleva la sesión persistente (diseño §4).
 */
export class TokenStore {
  private session: Session | null = null;
  /** `persist: false` (an app framed by Vitto): the refresh lives in memory only, for this document, never in localStorage. */
  private readonly persist: boolean;
  private memoryRefresh: string | null = null;

  constructor(options: { persist?: boolean } = {}) {
    this.persist = options.persist ?? true;
  }

  get access(): string | null {
    return this.session?.accessToken ?? null;
  }

  get expiresAt(): number | null {
    return this.session?.accessExpiresAt ?? null;
  }

  /** Puede devolver null: modo privado, almacenamiento bloqueado o primera visita. */
  get refresh(): string | null {
    if (!this.persist) return this.memoryRefresh;
    try {
      return globalThis.localStorage?.getItem(REFRESH_KEY) ?? null;
    } catch {
      return null;
    }
  }

  save(pair: TokenPair): void {
    this.session = {
      accessToken: pair.accessToken,
      accessExpiresAt: Date.parse(pair.accessExpiresAt),
    };
    this.writeRefresh(pair.refreshToken);
  }

  /** Adopta el par difundido por otra pestaña sin volver a escribir localStorage. */
  adopt(pair: TokenPair): void {
    this.session = {
      accessToken: pair.accessToken,
      accessExpiresAt: Date.parse(pair.accessExpiresAt),
    };
  }

  clear(): void {
    this.session = null;
    this.writeRefresh(null);
  }

  /** Borra solo la sesión en memoria: un fallo de red no debe costar el refresh guardado. */
  clearAccess(): void {
    this.session = null;
  }

  private writeRefresh(value: string | null): void {
    if (!this.persist) {
      this.memoryRefresh = value;
      return;
    }
    try {
      if (value === null) globalThis.localStorage?.removeItem(REFRESH_KEY);
      else globalThis.localStorage?.setItem(REFRESH_KEY, value);
    } catch {
      // Sin almacenamiento la sesión dura lo que la pestaña; no es motivo para romper la app.
    }
  }
}
